"use client";

import Script from "next/script";
import { useEffect, useRef } from "react";

interface TurnstileRenderOptions {
  sitekey: string;
  callback?: (token: string) => void;
  "expired-callback"?: () => void;
  "error-callback"?: () => void;
  theme?: "light" | "dark" | "auto";
  size?: "normal" | "compact" | "flexible";
  appearance?: "always" | "execute" | "interaction-only";
}

interface TurnstileApi {
  render: (
    container: HTMLElement | string,
    options: TurnstileRenderOptions,
  ) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId: string) => void;
  getResponse: (widgetId?: string) => string | undefined;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

interface TurnstileWidgetProps {
  siteKey: string;
  onToken: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
  resetSignal?: number;
  className?: string;
}

const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js";

const TurnstileWidget = ({
  siteKey,
  onToken,
  onExpire,
  onError,
  resetSignal,
  className,
}: TurnstileWidgetProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const renderedRef = useRef(false);
  const onTokenRef = useRef(onToken);
  const onExpireRef = useRef(onExpire);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onTokenRef.current = onToken;
    onExpireRef.current = onExpire;
    onErrorRef.current = onError;
  }, [onToken, onExpire, onError]);

  useEffect(() => {
    if (!siteKey || !containerRef.current) return;

    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;

    const tryRender = () => {
      if (cancelled) return;
      if (renderedRef.current) return;
      const api = window.turnstile;
      const container = containerRef.current;
      if (!api || !container) {
        pollTimer = setTimeout(tryRender, 150);
        return;
      }
      renderedRef.current = true;
      widgetIdRef.current = api.render(container, {
        sitekey: siteKey,
        callback: (token) => onTokenRef.current?.(token),
        "expired-callback": () => onExpireRef.current?.(),
        "error-callback": () => onErrorRef.current?.(),
        theme: "auto",
      });
    };

    tryRender();

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
      const id = widgetIdRef.current;
      if (id && window.turnstile) {
        try {
          window.turnstile.remove(id);
        } catch {
          // best-effort cleanup
        }
      }
      widgetIdRef.current = null;
      renderedRef.current = false;
    };
  }, [siteKey]);

  useEffect(() => {
    if (resetSignal === undefined) return;
    const id = widgetIdRef.current;
    if (id && window.turnstile) {
      try {
        window.turnstile.reset(id);
      } catch {
        // ignore
      }
    }
  }, [resetSignal]);

  return (
    <>
      <Script src={TURNSTILE_SCRIPT_SRC} strategy="lazyOnload" async defer />
      <div ref={containerRef} className={className} />
    </>
  );
};

export default TurnstileWidget;
